import {oneOf, shape, string} from 'prop-types';
import {pluck} from 'ramda';
import domainData from '@sinclair-digital/stl-common/src/models/domainData';
import {Validator, toValidatedField} from '@sinclair-digital/stl-common/src/ValidatedField';
import {
    validatorShape,
    validateRequiredFields,
    hasErrors as hasValidationErrors,
} from '@sinclair-digital/sbg-storyline-components/models';
import {PropertySiteV1} from '@sinclair-digital/stl-common/src/models/Property';

const usStates = domainData.usStates;
const stateList = pluck('abbreviation', usStates);

const dmas = domainData.designatedMarketAreas;
const dmaNames = pluck('dma_name', dmas);

const timeZoneData = domainData.usTimeZones;
const timezones = pluck('shortName', timeZoneData);

const propertyNetworks = domainData.propertyNetworks;
const networks = pluck('name', propertyNetworks);
const generalShape = {footerText: string};

export interface GeneralSettings {
    uuid?: string; // undefined prior to POST
    name: Validator<string>;
    city: Validator<string>;
    state: string;
    zipcode: Validator<string>;
    callLetters: Validator<string>;
    url: Validator<string>;
    designatedMarketArea: string;
    channel: Validator<string>;
    timezone: string;
    stagingUrl: string;
    network: string;
    footerText: Validator<string>;
    imageUrl: string | null | undefined;
}

export const generalOptics = {
    name: 'name',
    city: 'city',
    callLetters: 'callLetters',
    state: 'state',
    zipcode: 'zipcode',
    url: 'url',
    timezone: 'timezone',
    network: 'network',
    designatedMarketArea: 'designatedMarketArea',
    channel: 'channel',
    imageUrl: 'imageUrl',
    footerText: 'footerText',
    stagingUrl: 'stagingUrl',
};

export const generalSettingsShape = {
    name: shape(validatorShape(string)),
    city: shape(validatorShape(string)),
    state: oneOf(stateList),
    zipcode: shape(validatorShape(string)),
    callLetters: shape(validatorShape(string)),
    url: shape(validatorShape(string)),
    designatedMarketArea: oneOf(dmaNames),
    channel: shape(validatorShape(string)),
    timezone: oneOf(timezones),
    stagingUrl: string,
    network: oneOf(networks),
    general: shape(generalShape),
    imageUrl: string,
    footerText: shape(validatorShape(string)),
};

export const defaultGeneralSettings: GeneralSettings = {
    name: toValidatedField(''),
    city: toValidatedField(''),
    callLetters: toValidatedField(''),
    state: stateList[0],
    zipcode: toValidatedField(''),
    url: toValidatedField(''),
    stagingUrl: '',
    timezone: timezones[0],
    network: networks[0],
    designatedMarketArea: dmaNames[0],
    channel: toValidatedField(''),
    imageUrl: '',
    footerText: toValidatedField(''),
};

/**
 * Convert server model into client model
 */
export const parseGeneral = ({
    general: {footerText = ''},
    name,
    city,
    callLetters,
    state,
    zipcode,
    url,
    timezone,
    network,
    designatedMarketArea,
    channel,
    imageUrl,
    stagingUrl,
    uuid,
}: PropertySiteV1): GeneralSettings => ({
    uuid,
    name: toValidatedField(name),
    city: toValidatedField(city),
    callLetters: toValidatedField(callLetters),
    state,
    zipcode: toValidatedField(zipcode),
    url: toValidatedField(url),
    timezone,
    network,
    designatedMarketArea,
    channel: toValidatedField(channel),
    imageUrl,
    stagingUrl,
    footerText: toValidatedField(footerText),
});

export const serializeGeneral = ({
    name,
    city,
    callLetters,
    state,
    zipcode,
    url,
    timezone,
    network,
    designatedMarketArea,
    channel,
    imageUrl,
    stagingUrl,
    uuid,
    footerText,
}: GeneralSettings) => ({
    name: name.value,
    city: city.value,
    callLetters: callLetters.value,
    state,
    zipcode: zipcode.value,
    url: url.value,
    timezone,
    network,
    designatedMarketArea,
    channel: channel.value,
    imageUrl,
    stagingUrl,
    uuid,
    general: {footerText: footerText.value},
});

const requiredFields = [
    generalOptics.name,
    generalOptics.city,
    generalOptics.zipcode,
    generalOptics.callLetters,
    generalOptics.url,
    generalOptics.channel,
    generalOptics.footerText,
];

export const validatedFields = requiredFields;

export const validate = validateRequiredFields(requiredFields);

export const hasGeneralSettingsErrors = hasValidationErrors(validatedFields);
